/**
 * БИН или ИИН Республики Казахстан: 12 цифр с контрольной суммой.
 *
 * Проверка контрольной суммы, а не только длины: без неё в реквизитах
 * оказывается `123456789012` у каждого, кто не захотел искать свидетельство.
 * Разбирать такое потом приходится поддержке вручную, по одному.
 *
 * Алгоритм официальный: сумма произведений первых одиннадцати цифр на веса
 * 1…11 по модулю 11 даёт двенадцатую цифру. Если остаток равен 10, счёт
 * повторяется со вторым набором весов; если и он даёт 10 — такого номера
 * не существует.
 *
 * Тот же расчёт живёт на бэке (`App\Rules\RegistrationNumber`) и в мобильном
 * приложении (`ValidationUtils.isValidBin`). Дублируется намеренно: клиент
 * показывает ошибку сразу, не дожидаясь запроса, но доверенной стороной не
 * является — сервер проверяет заново.
 */

/** Основной набор весов. */
const WEIGHTS_PRIMARY = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/** Запасной — когда основной дал остаток 10. */
const WEIGHTS_FALLBACK = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];

/** Формат: ровно 12 цифр. У БИН и ИИН он общий, различает их форма собственности. */
export const REGISTRATION_NUMBER_PATTERN = /^\d{12}$/;

function weightedSum(digits: number[], weights: number[]): number {
  return weights.reduce((sum, weight, index) => sum + digits[index] * weight, 0);
}

/** Сходится ли контрольная цифра. */
export function isValidRegistrationNumber(value: string): boolean {
  if (!REGISTRATION_NUMBER_PATTERN.test(value)) return false;

  const digits = value.split('').map(Number);
  const control = digits[11];

  let checksum = weightedSum(digits, WEIGHTS_PRIMARY) % 11;
  if (checksum === 10) {
    checksum = weightedSum(digits, WEIGHTS_FALLBACK) % 11;
  }

  // Оба набора дали 10 — номер невозможен: контрольная цифра одна,
  // десяткой она быть не может.
  if (checksum === 10) return false;

  return checksum === control;
}

/**
 * Сообщение об ошибке для формы или `null`, если номер годится.
 *
 * Тексты те же, что показывает мобильное приложение: партнёр, заполнявший
 * анкету в телефоне и повторяющий её в вебе, не должен читать две разные
 * формулировки об одной и той же ошибке.
 */
export function validateRegistrationNumber(
  value: string,
  identifierLabel = 'БИН / ИИН'
): string | null {
  const trimmed = value.trim();

  if (!trimmed) return `Укажите ${identifierLabel}`;
  if (!REGISTRATION_NUMBER_PATTERN.test(trimmed)) return 'Должно быть 12 цифр';
  if (!isValidRegistrationNumber(trimmed)) return 'Такого номера не существует — проверьте';

  return null;
}
